import type React from 'react';
import { useCallback, useRef, useState } from 'react';
import isEqual from 'lodash/isEqual';

/**
 * Works just like useState except that it uses deep comparison to determine if the state has changed.
 */
export const useStateDeepCompare = <T>(
    initialValue?: T,
): [T | undefined, React.Dispatch<React.SetStateAction<T | undefined>>] => {
    const [state, setState] = useState<T | undefined>(initialValue);
    const prevState = useRef(initialValue);

    const setStateDeepCompare = useCallback((newState: React.SetStateAction<T | undefined>) => {
        const resolvedNewState =
            typeof newState === 'function'
                ? (newState as (prevState: T | undefined) => T | undefined)(prevState.current)
                : newState;

        if (!isEqual(prevState.current, resolvedNewState)) {
            prevState.current = resolvedNewState;
            setState(resolvedNewState);
        }
    }, []);

    return [state, setStateDeepCompare];
};
